import type { SeasonMilestone } from './season';

export type PerkId =
  | 'damage_up'
  | 'hp_up'
  | 'speed_up'
  | 'fire_rate_up'
  | 'crit_chance'
  | 'regen'
  | 'piercing'
  | 'ricochet'
  | 'magnetic'
  | 'vampiric'
  | 'explosive_kills'
  | 'shield_cycle'
  // Phase 2q: Archero-style projectile transformers (must stay in sync with
  // apps/web/src/game/systems/perks.ts PerkId — this is the persisted/API canon).
  | 'multishot_forward'
  | 'side_shots'
  | 'diagonal_shots'
  | 'rear_shot'
  | 'bouncy_walls'
  | 'big_bullet'
  | 'skewer'
  | 'orbit_blade'
  // Phase 2r
  | 'ignite'
  | 'toxin'
  | 'chain_lightning'
  | 'cryo';

export const PERK_IDS: readonly PerkId[] = [
  'damage_up', 'hp_up', 'speed_up', 'fire_rate_up',
  'crit_chance', 'regen', 'piercing', 'ricochet',
  'magnetic', 'vampiric', 'explosive_kills', 'shield_cycle',
  // Phase 2q
  'multishot_forward', 'side_shots', 'diagonal_shots', 'rear_shot',
  'bouncy_walls', 'big_bullet', 'skewer', 'orbit_blade',
  // Phase 2r
  'ignite', 'toxin', 'chain_lightning', 'cryo',
];

export type RunOutcome = 'won' | 'lost';

// Phase 2h forward declaration — used in RunSubmission below
export type BossId = 'aegis_defender' | 'bastet';

/**
 * What the client posts to POST /runs.
 *
 * `clientScore` is what the client computed locally; the server recomputes
 * and rejects if the drift exceeds the scoring tolerance.
 */
export type RunSubmission = {
  seed: number;
  outcome: RunOutcome;
  kills: number;
  loot: number;
  durationMs: number;
  perks: PerkId[];
  /** Phase 2u: per-choice offered-but-skipped perks. Index = offerIndex.
   *  Each inner array contains the 2 perks NOT picked from that offer.
   *  Optional for backward compat with older clients. */
  offeredSkipped?: PerkId[][];
  clientScore: number;
  catClassId?: CatClassId;
  /** Items used mid-run; server validates each is in loadout at submit time. */
  consumablesUsed?: ConsumableId[];
  /** Items picked up in-run; applied to inventory only on outcome=won (capped 10). */
  consumablesPicked?: ConsumableId[];
  /** Phase 2h: present only on `outcome === 'won'` and only if a boss was killed. */
  bossDefeated?: BossId;
  /** Phase 2i: present when the run was played as a Daily Challenge entry. */
  challengeRun?: ChallengeRunSubmission;
};

export type RunRecord = {
  id: string;
  playerId: string;
  outcome: RunOutcome;
  kills: number;
  loot: number;
  perks: PerkId[];
  durationMs: number;
  seed: number;
  score: number;
  createdAt: number;
  catClassId: CatClassId | null;
  /** Phase 2h: which boss (if any) was defeated on this run. Only present when outcome === 'won'. */
  bossDefeated?: BossId;
};

export type PlayerProgress = {
  playerId: string;
  totalRuns: number;
  wins: number;
  totalKills: number;
  totalLoot: number;
  bestKills: number;
  bestScore: number;
  uniquePerks: PerkId[];
  updatedAt: number;
};

export type SubmitRunResponse = {
  run: RunRecord;
  progress: PlayerProgress;
  fishGained: number;
  catUpdate?: RunCatUpdate;
  /** Phase 2g — updated consumables snapshot when used or picked is non-empty. */
  consumables?: ConsumablesResponse;
  /** Phase 2h: computed server-side via run history aggregation. */
  achievements?: AchievementsState;
  /** Phase 2i: present only when the run was a valid Daily Challenge submission. */
  challenge?: ChallengeResult;
};

export type ProgressResponse = {
  progress: PlayerProgress;
  recentRuns: RunRecord[];   // newest first, up to 20
  /** Phase 2h: computed server-side via run history aggregation. */
  achievements?: AchievementsState;
};

/**
 * Canonical run-score formula used by both client (local preview) and server
 * (anti-cheat recompute). Must stay in sync — drift causes honest clients to
 * fail the tolerance check.
 */
export function computeRunScore(input: {
  outcome: RunOutcome;
  kills: number;
  loot: number;
  durationMs: number;
}): number {
  const killScore = input.kills * 10;
  const lootScore = input.loot * 5;
  const winBonus = input.outcome === 'won' ? 500 : 0;
  const durationPenalty = Math.floor(Math.max(0, input.durationMs) / 1000);
  return killScore + lootScore + winBonus - durationPenalty;
}

/** ISO date YYYY-MM-DD (UTC). Used as `daily_runs.date` primary key. */
export type DateString = string;

export type DailyRun = {
  date: DateString;
  seed: number;
  generatedAt: number;
};

/**
 * Optional field on RunSubmission: if present and matches today's UTC date,
 * the server records a leaderboard entry for that day.
 */
export type RunSubmissionWithDaily = RunSubmission & {
  dailyRunDate?: DateString;
};

export type LeaderboardEntry = {
  playerId: string;
  tgUsername: string | null;
  tgFirstName: string | null;
  score: number;
  runId: string;
  submittedAt: number;
  rank: number;
};

export type LeaderboardResponse = {
  date: DateString;
  entries: LeaderboardEntry[];
  totalEntries: number;
};

export type LeaderboardMeResponse = {
  date: DateString;
  me: LeaderboardEntry | null;
  neighbours: LeaderboardEntry[]; // up to 2 above + me + up to 2 below (max 5)
  totalEntries: number;
};

export type Streak = {
  currentStreak: number;
  longestStreak: number;
  lastLoginDate: DateString;
};

export type QuestId = 'kill_50' | 'flawless_run' | 'perk_collector' | 'marathon_run' | 'win_one' | 'loot_hunter';

/** Static definition of every quest the game can offer. */
export type QuestDef = {
  id: QuestId;
  nameKey: string;
  descKey: string;
  target: number;
  rewardFishies: number;
};

export const QUEST_POOL: readonly QuestDef[] = [
  { id: 'kill_50',         nameKey: 'quest.kill_50.name',         descKey: 'quest.kill_50.desc',         target: 50, rewardFishies: 20 },
  { id: 'flawless_run',    nameKey: 'quest.flawless_run.name',    descKey: 'quest.flawless_run.desc',    target: 1,  rewardFishies: 30 },
  { id: 'perk_collector',  nameKey: 'quest.perk_collector.name',  descKey: 'quest.perk_collector.desc',  target: 3,  rewardFishies: 15 },
  { id: 'marathon_run',    nameKey: 'quest.marathon_run.name',    descKey: 'quest.marathon_run.desc',    target: 5,  rewardFishies: 25 },
  { id: 'win_one',         nameKey: 'quest.win_one.name',         descKey: 'quest.win_one.desc',         target: 1,  rewardFishies: 20 },
  { id: 'loot_hunter',     nameKey: 'quest.loot_hunter.name',     descKey: 'quest.loot_hunter.desc',     target: 10, rewardFishies: 15 },
];

export type QuestStatus = {
  id: QuestId;
  nameKey: string;
  descKey: string;
  progress: number;
  target: number;
  rewardFishies: number;
  completed: boolean;
  claimed: boolean;
};

export type DailyStatusResponse = {
  date: DateString;
  streak: Streak;
  quests: QuestStatus[];
  fishies: number;
};

export type ClaimQuestRequest = {
  questId: QuestId;
};

export type ClaimQuestResponse = {
  questId: QuestId;
  fishiesAwarded: number;
  totalFishies: number;
};

export type TelemetryEventKind =
  | 'window_error'
  | 'unhandled_rejection'
  | 'react_error_boundary'
  | 'manual';

export type TelemetryEventRequest = {
  event: TelemetryEventKind;
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  appVersion?: string;
};

export type TelemetryEvent = TelemetryEventRequest & {
  id: string;
  playerId: string | null;
  occurredAt: number;
};

export type TelemetryReportResponse = { ok: true; id: string };

export type TelemetryRecentResponse = {
  events: TelemetryEvent[];
};

/** What the server returns from POST /runs/:runId/share-card. */
export type ShareCardResponse = {
  runId: string;
  shareUrl: string;       // public HTML page (TG OG renders this)
  imageUrl: string;       // direct SVG of the card
  deepLink: string;       // https://t.me/<bot>?startapp=... link
};

/** Deep-link routing intent parsed from `start_param`. */
export type StartParamIntent =
  | { kind: 'none' }
  | { kind: 'daily' }
  | { kind: 'challenge'; seed: number }
  | { kind: 'unknown'; raw: string };

/** Public-friendly subset embedded in the SVG/HTML card. */
export type ShareCardPayload = {
  runId: string;
  tgUsername: string | null;
  tgFirstName: string | null;
  outcome: RunOutcome;
  kills: number;
  loot: number;
  score: number;
  perks: PerkId[];
  durationMs: number;
  seed: number;
};

/** Identifier for one of the 7 hideout modules. */
export type HideoutModuleId =
  | 'cryo'
  | 'med'
  | 'lab'
  | 'gym'
  | 'galley'
  | 'hangar'
  | 'bridge';

export const HIDEOUT_MODULE_IDS: readonly HideoutModuleId[] = [
  'cryo', 'med', 'lab', 'gym', 'galley', 'hangar', 'bridge',
];

/** Bundled gameplay effects derived from the player's full module state.
 * Server-derived single source of truth; client never re-computes from raw levels. */
export type HideoutEffects = {
  /** Bonus HP added to maxHp at run start. */
  hpBonus: number;
  /** Number of revives available during a run (auto-restore HP to 50% on death, then decrement). */
  extraRevives: number;
  /** Additional perk options shown at level-up picker (Phase 0 default is 3). */
  perkPickerBonus: number;
  /** Multiplier on weapon damage (1.0 = unchanged). */
  damageMul: number;
  /** Server-side multiplier on loot→fish conversion at run extraction. */
  lootFishMul: number;
  /** Number of random perks pre-selected and auto-applied at run start. */
  preSelectedPerks: number;
  /** Number of perk-picker rerolls available during a run. */
  rerolls: number;
};

export const ZERO_EFFECTS: HideoutEffects = {
  hpBonus: 0,
  extraRevives: 0,
  perkPickerBonus: 0,
  damageMul: 1,
  lootFishMul: 1,
  preSelectedPerks: 0,
  rerolls: 0,
};

/** One module's state in the response. */
export type HideoutModuleState = {
  id: HideoutModuleId;
  /** Current upgrade level, 0–10. */
  level: number;
  /** Cost in fish to reach `level + 1`. `null` when `atMaxLevel`. */
  nextCost: number | null;
  /** Derived convenience flag — equivalent to `nextCost === null`. Server is the
   * single writer so the two fields stay aligned; UI code reads `atMaxLevel`
   * for semantic clarity ("can I upgrade?") instead of probing `nextCost`. */
  atMaxLevel: boolean;
};

/** Full hideout state for the authenticated player. */
export type HideoutResponse = {
  modules: HideoutModuleState[];
  effects: HideoutEffects;
  fishies: number;
};

/** POST /hideout/upgrade response — identical shape to HideoutResponse plus the upgraded id. */
export type HideoutUpgradeResult = HideoutResponse & {
  upgradedId: HideoutModuleId;
  newLevel: number;
};

// ---------------------------------------------------------------------------
// Cat Roster — Phase 2b
// ---------------------------------------------------------------------------

export type CatClassId = 'vanguard' | 'netrunner' | 'psyker' | 'engineer';

export const CAT_CLASS_IDS: readonly CatClassId[] = [
  'vanguard', 'netrunner', 'psyker', 'engineer',
];

export type CatClassStatDeltas = {
  maxHpMul: number;
  damageMul: number;
  moveSpeedMul: number;
  fireRateMul: number;
};

export type CatClassDef = {
  id: CatClassId;
  /** i18n key for class name */
  nameKey: string;
  /** i18n key for one-line role description */
  descKey: string;
  /** Emoji icon for UI */
  icon: string;
  stats: CatClassStatDeltas;
  /** Perks applied via applyPerk() at run start, in this order, BEFORE Hangar pre-perks */
  startingPerks: PerkId[];
};

export const CAT_CLASSES: readonly CatClassDef[] = [
  {
    id: 'vanguard',
    nameKey: 'cat.vanguard.name',
    descKey: 'cat.vanguard.desc',
    icon: '🛡️',
    stats: { maxHpMul: 1.50, damageMul: 0.85, moveSpeedMul: 0.85, fireRateMul: 1.00 },
    startingPerks: ['hp_up'],
  },
  {
    id: 'netrunner',
    nameKey: 'cat.netrunner.name',
    descKey: 'cat.netrunner.desc',
    icon: '🌐',
    stats: { maxHpMul: 0.80, damageMul: 1.00, moveSpeedMul: 1.20, fireRateMul: 1.30 },
    startingPerks: ['speed_up'],
  },
  {
    id: 'psyker',
    nameKey: 'cat.psyker.name',
    descKey: 'cat.psyker.desc',
    icon: '🔮',
    stats: { maxHpMul: 1.10, damageMul: 0.90, moveSpeedMul: 1.00, fireRateMul: 1.00 },
    startingPerks: ['regen', 'piercing'],
  },
  {
    id: 'engineer',
    nameKey: 'cat.engineer.name',
    descKey: 'cat.engineer.desc',
    icon: '🔧',
    stats: { maxHpMul: 1.00, damageMul: 1.00, moveSpeedMul: 1.00, fireRateMul: 1.00 },
    startingPerks: ['crit_chance', 'ricochet'],
  },
];

export const DEFAULT_CAT_CLASS_ID: CatClassId = 'engineer';

/** GET /me/loadout response shape. */
export type LoadoutResponse = {
  /** All perks the player has ever unlocked, sorted newest-first by unlock time. */
  unlockedPerks: PerkId[];
  /** Currently selected starter perk for next run; null = no loadout. */
  loadoutPerkId: PerkId | null;
};

// ============== Phase 2f: 9 Lives + Scars ==============

export type ScarId =
  | 'frail'
  | 'weak_grip'
  | 'lame_paw'
  | 'flinch'
  | 'glass_jaw';

export const SCAR_IDS: readonly ScarId[] = ['frail', 'weak_grip', 'lame_paw', 'flinch', 'glass_jaw'];

export type MutationId =
  | 'iron_skin'
  | 'feral_strength'
  | 'swift_steps'
  | 'keen_eye'
  | 'nine_lives_echo';

export const MUTATION_IDS: readonly MutationId[] = ['iron_skin', 'feral_strength', 'swift_steps', 'keen_eye', 'nine_lives_echo'];

export type ScarMutationEffect = {
  maxHpMul?: number;
  damageMul?: number;
  moveSpeedMul?: number;
  perkPickerDelta?: number;
  firstHitMul?: number;
  bonusRevives?: number;
};

export type ScarDef = { id: ScarId; nameKey: string; descKey: string; effect: ScarMutationEffect };
export type MutationDef = { id: MutationId; nameKey: string; descKey: string; effect: ScarMutationEffect };

export const SCAR_DEFS: readonly ScarDef[] = [
  { id: 'frail',     nameKey: 'scar.frail.name',     descKey: 'scar.frail.desc',     effect: { maxHpMul: 0.95 } },
  { id: 'weak_grip', nameKey: 'scar.weak_grip.name', descKey: 'scar.weak_grip.desc', effect: { damageMul: 0.90 } },
  { id: 'lame_paw',  nameKey: 'scar.lame_paw.name',  descKey: 'scar.lame_paw.desc',  effect: { moveSpeedMul: 0.90 } },
  { id: 'flinch',    nameKey: 'scar.flinch.name',    descKey: 'scar.flinch.desc',    effect: { perkPickerDelta: -1 } },
  { id: 'glass_jaw', nameKey: 'scar.glass_jaw.name', descKey: 'scar.glass_jaw.desc', effect: { firstHitMul: 2.0 } },
];

export const MUTATION_DEFS: readonly MutationDef[] = [
  { id: 'iron_skin',         nameKey: 'mut.iron_skin.name',         descKey: 'mut.iron_skin.desc',         effect: { maxHpMul: 1.05 } },
  { id: 'feral_strength',    nameKey: 'mut.feral_strength.name',    descKey: 'mut.feral_strength.desc',    effect: { damageMul: 1.10 } },
  { id: 'swift_steps',       nameKey: 'mut.swift_steps.name',       descKey: 'mut.swift_steps.desc',       effect: { moveSpeedMul: 1.10 } },
  { id: 'keen_eye',          nameKey: 'mut.keen_eye.name',          descKey: 'mut.keen_eye.desc',          effect: { perkPickerDelta: 1 } },
  { id: 'nine_lives_echo',   nameKey: 'mut.nine_lives_echo.name',   descKey: 'mut.nine_lives_echo.desc',   effect: { bonusRevives: 1 } },
];

/** Active or retired cat record returned from /me/cats. */
export type CatRecord = {
  classId: CatClassId;
  livesRemaining: number;
  scars: ScarId[];
  mutations: MutationId[];
  runsPlayed: number;
  createdAt: number;
  retiredAt?: number;  // undefined = active
};

export type CatsResponse = {
  active: CatRecord[];   // 0-4 entries
  retired: CatRecord[];  // desc by retiredAt
};

// ============================================================================
// Phase 2g — Consumables
// ============================================================================

export type ConsumableId =
  | 'med_injector'
  | 'emp_grenade'
  | 'shield_recharge'
  | 'nano_stim';

export const CONSUMABLE_IDS: readonly ConsumableId[] = [
  'med_injector',
  'emp_grenade',
  'shield_recharge',
  'nano_stim',
];

export type ConsumableEffect =
  | { kind: 'heal'; amount: number }
  | { kind: 'emp'; radius: number; durationMs: number }
  | { kind: 'shield'; charges: number; fallbackIframeMs: number }
  | { kind: 'buff'; damageMul: number; moveSpeedMul: number; durationMs: number };

export type ConsumableDef = {
  id: ConsumableId;
  nameKey: string;
  descKey: string;
  cost: number;
  effect: ConsumableEffect;
};

export const CONSUMABLE_DEFS: readonly ConsumableDef[] = [
  { id: 'med_injector',    nameKey: 'consumable.med_injector.name',    descKey: 'consumable.med_injector.desc',    cost: 50,  effect: { kind: 'heal', amount: 40 } },
  { id: 'emp_grenade',     nameKey: 'consumable.emp_grenade.name',     descKey: 'consumable.emp_grenade.desc',     cost: 80,  effect: { kind: 'emp', radius: 200, durationMs: 3000 } },
  { id: 'shield_recharge', nameKey: 'consumable.shield_recharge.name', descKey: 'consumable.shield_recharge.desc', cost: 60,  effect: { kind: 'shield', charges: 1, fallbackIframeMs: 4000 } },
  { id: 'nano_stim',       nameKey: 'consumable.nano_stim.name',       descKey: 'consumable.nano_stim.desc',       cost: 100, effect: { kind: 'buff', damageMul: 1.5, moveSpeedMul: 1.5, durationMs: 5000 } },
];

/** Tuple shape — always exactly 3 entries, nulls padded. */
export type ConsumableLoadout = [
  ConsumableId | null,
  ConsumableId | null,
  ConsumableId | null,
];

/** GET /me/consumables response. */
export type ConsumablesResponse = {
  inventory: Partial<Record<ConsumableId, number>>;
  loadout: ConsumableLoadout;
};

/** POST /consumables/buy response — extends ConsumablesResponse with the new fish balance. */
export type BuyConsumableResponse = ConsumablesResponse & { fishies: number };

/** Updated portion of POST /runs response. */
export type RunCatUpdate = {
  classId: CatClassId;
  livesRemaining: number;
  scars: ScarId[];
  mutations: MutationId[];
  runsPlayed: number;
  retiredAt: number | null;
  rolled: { kind: 'scar'; id: ScarId } | { kind: 'mutation'; id: MutationId } | null;
};

// ============================================================================
// Phase 2h — Bastet boss arc
// ============================================================================

export const BOSS_IDS: readonly BossId[] = ['aegis_defender', 'bastet'];

/** Lifetime won-runs with kills>=25 needed to unlock the Reactor Floor. */
export const REACTOR_UNLOCK_THRESHOLD = 5;

export type AchievementsState = {
  bastetDefeated: boolean;
  fullClearCount: number;
  reactorEntered: boolean;
};

/**
 * `collisionImmortal` — flag that contact-with-cat does no damage AND does not
 * destroy Bastet. Bullets still damage her normally; the name reflects that the
 * runtime only short-circuits the collision handler (not the bullet kill path).
 */
export type BastetPhase1Tunables = {
  moveSpeed: number;
  sentrySpawnIntervalSec: number;
  projectileFanIntervalSec: number;
  fanShots: number;
  collisionImmortal: true;
};
export type BastetPhase2Tunables = {
  moveSpeed: number;
  wormSpawnIntervalSec: number;
  projectileBurstIntervalSec: number;
  burstShots: number;
  collisionImmortal: false;
  collisionDamage: number;
};

export const BASTET_PHASE_TUNABLES: { 1: BastetPhase1Tunables; 2: BastetPhase2Tunables } = {
  1: { moveSpeed: 0,  sentrySpawnIntervalSec: 4, projectileFanIntervalSec: 5, fanShots: 5, collisionImmortal: true },
  2: { moveSpeed: 40, wormSpawnIntervalSec: 6, projectileBurstIntervalSec: 7, burstShots: 12, collisionImmortal: false, collisionDamage: 30 },
};

/**
 * Russian dialog script for the Bastet boss arc. RU-only by design
 * (matches the RU-only ship). Future EN localisation, if needed,
 * would be a parallel const named BASTET_DIALOG_EN.
 */
export const BASTET_DIALOG_RU = {
  intro: [
    'Так. Ты пришёл.',
    'Я надеялась, что ты будешь умнее. Что ты увидишь долю в моих контрактах.',
    'Каждая жизнь, которую я тебе вернула — была моей. Каждая рыбка — мой долг тебе.',
    'Уйди. Я ещё могу выпустить тебя.',
  ],
  transition: [
    'Нет?',
    'Хорошо. Я тоже устала ждать.',
    'Знаешь, что у меня внутри? Земля. То, от чего ты бежал.',
  ],
  defeat: [
    'Постой... ты не понимаешь. Я держала Землю в себе. Я была щитом.',
    'Теперь ты — щит. Удачи, котёнок.',
  ],
} as const;

// ============================================================
// Phase 2i — Daily Challenge
// ============================================================

export type ChallengeModifierId =
  | 'double_sentry'
  | 'glass_cat'
  | 'fast_waves'
  | 'swarm_only'
  | 'fishie_storm'
  | 'class_lock_engineer'
  | 'no_perks';

export const MODIFIER_IDS = [
  'double_sentry', 'glass_cat', 'fast_waves', 'swarm_only',
  'fishie_storm', 'class_lock_engineer', 'no_perks',
] as const satisfies readonly ChallengeModifierId[];

export type ChallengeModifierDef = {
  id: ChallengeModifierId;
  scoreMultiplier: number;
};

export const MODIFIER_DEFS: Record<ChallengeModifierId, ChallengeModifierDef> = {
  double_sentry:       { id: 'double_sentry',       scoreMultiplier: 1.10 },
  glass_cat:           { id: 'glass_cat',           scoreMultiplier: 1.40 },
  fast_waves:          { id: 'fast_waves',          scoreMultiplier: 1.25 },
  swarm_only:          { id: 'swarm_only',          scoreMultiplier: 0.85 },
  fishie_storm:        { id: 'fishie_storm',        scoreMultiplier: 1.20 },
  class_lock_engineer: { id: 'class_lock_engineer', scoreMultiplier: 1.15 },
  no_perks:            { id: 'no_perks',            scoreMultiplier: 1.30 },
};

/**
 * FNV-1a 32-bit hash. Private — used only by `pickDailyModifier` to map an
 * ISO date string to a deterministic modifier index. If you need this
 * elsewhere, lift it to a dedicated `hash.ts` rather than re-importing.
 */
function fnv1aHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Deterministically pick the daily-challenge modifier for an ISO date
 * (YYYY-MM-DD, UTC). Same input → same output; client and server must
 * stay byte-identical, so the hash is locked to FNV-1a.
 */
export function pickDailyModifier(date: string): ChallengeModifierId {
  const h = fnv1aHash(date);
  return MODIFIER_IDS[h % MODIFIER_IDS.length]!;
}

/**
 * Apply a challenge modifier's score multiplier with floor rounding.
 * Used by both client (preview) and server (canonical) — must not drift.
 */
export function scaleChallengeScore(rawScore: number, modifierId: ChallengeModifierId): number {
  return Math.floor(rawScore * MODIFIER_DEFS[modifierId].scoreMultiplier);
}

export type ChallengeRunSubmission = {
  date: string;
  modifierId: ChallengeModifierId;
};

export type ChallengeResult = {
  rank: number;
  score: number;        // scaled
  rawScore: number;
  streak: number;
  fishiesAwarded: number;
};

/**
 * One row in the Daily Challenge leaderboard. Mostly overlaps with
 * `LeaderboardEntry` but adds challenge-specific fields (rawScore, kills,
 * loot, outcome) so the UI can render rich rows without a second fetch.
 */
export type ChallengeEntry = {
  playerId: string;
  tgUsername: string | null;
  tgFirstName: string | null;
  score: number;
  rawScore: number;
  kills: number;
  loot: number;
  outcome: RunOutcome;
  runId: string;
  submittedAt: number;
  rank: number;
};

/**
 * GET /challenge/today — lazy-creates the day's challenge, returns the
 * player's entry (if any), the top-10 leaderboard plus a small neighbour
 * window when the player is outside the top-10, and the prize tier config.
 */
export type ChallengeTodayResponse = {
  date: DateString;
  modifierId: ChallengeModifierId;
  seed: number;
  yourEntry: ChallengeEntry | null;
  yourRank: number | null;
  leaderboard: ChallengeEntry[];
  prizeTiers: {
    topTen: number;
    participation: number;
  };
};

// ============================================================
// Phase 2k — Tournament Weekend
// ============================================================

/**
 * True when `date` (YYYY-MM-DD, UTC) falls on a Friday, Saturday, or Sunday —
 * the three days that make up a Tournament Weekend. We parse the input as a
 * UTC date so the result is independent of the server's local timezone.
 */
export function isTournamentWeekend(date: string): boolean {
  // 5 = Friday, 6 = Saturday, 0 = Sunday in JS Date.getUTCDay()
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 5 || day === 6 || day === 0;
}

/**
 * Returns the ISO date of the Friday that begins the Tournament Weekend
 * containing `date`. Throws when `date` is not a weekend day — callers must
 * gate with `isTournamentWeekend` first.
 */
export function tournamentWeekendStart(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDay();
  let offset: number;
  if (day === 5) offset = 0;
  else if (day === 6) offset = 1;
  else if (day === 0) offset = 2;
  else throw new Error(`not a weekend date: ${date}`);
  const friday = new Date(d.getTime() - offset * 86_400_000);
  return friday.toISOString().slice(0, 10);
}

/**
 * Expand a Friday weekend-start into the [Fri, Sat, Sun] tuple of ISO dates.
 * Used by SQL aggregates that need to bind all three dates.
 */
export function tournamentWeekendDates(
  weekendStart: string,
): readonly [string, string, string] {
  const fri = new Date(`${weekendStart}T00:00:00Z`);
  return [
    weekendStart,
    new Date(fri.getTime() + 86_400_000).toISOString().slice(0, 10),
    new Date(fri.getTime() + 2 * 86_400_000).toISOString().slice(0, 10),
  ] as const;
}

/**
 * Deterministic sticky modifier for an entire Tournament Weekend. Hashes the
 * Friday's date so Fri/Sat/Sun of the same weekend all yield the same modifier.
 * Reuses `pickDailyModifier` so the modifier distribution mirrors regular
 * dailies — the only difference is the keyed input.
 */
export function pickTournamentModifier(weekendStart: string): ChallengeModifierId {
  return pickDailyModifier(weekendStart);
}

export type TournamentEntry = {
  playerId: string;
  tgUsername: string | null;
  tgFirstName: string | null;
  cumulativeScore: number;
  daysPlayed: number;   // 1..3 — how many days of this weekend the player submitted
  rank: number;
};

// ============================================================
// Phase 3l — Season + Energy response types
// ============================================================

export type SeasonResponse = {
  seasonId: string;
  xp: number;
  level: number;
  milestones: SeasonMilestone[];
  claimedLevels: number[];
  claimableLevels: number[];
};

export type ClaimMilestoneResponse = { claimedLevels: number[]; fishies: number };

export type EnergyResponse = { current: number; cap: number; lastRefillAt: number; msUntilNext: number };

export type TournamentCurrentResponse = {
  isActive: boolean;
  weekendStart: string | null;          // ISO date of Friday
  weekendEnd: string | null;            // ISO date of Sunday
  modifierId: ChallengeModifierId | null;
  daysRemaining: number;                // 3 on Fri, 2 on Sat, 1 on Sun, 0 outside
  yourCumulativeScore: number | null;
  yourCumulativeRank: number | null;
  leaderboard: TournamentEntry[];
  prizeTiers: { topTenWeekend: number; participationDaily: number };
};
